import { useEffect, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchAuthenticatedBlob } from '../../../../api/client';
import { Icon } from '../../../../components/Icon';
import type { VisualizationMediaItem } from '../../../../types/admin';
import { useAuth } from '../../../auth/AuthProvider';
import styles from '../StepPanel.module.scss';

// ─── Media grid ───────────────────────────────────────────────────────────────

type MediaGridProps = {
  readonly items: readonly VisualizationMediaItem[];
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stageId: string;
  readonly onDelete: (item: VisualizationMediaItem) => void;
  readonly onReorder: (orderedIds: readonly string[]) => void;
  readonly isMutating: boolean;
  readonly deletingId: string | null;
};

export function MediaGrid({
  items,
  patientId,
  treatmentId,
  stageId,
  onDelete,
  onReorder,
  isMutating,
  deletingId,
}: MediaGridProps) {
  const { t } = useTranslation();
  const { tokens } = useAuth();
  const token = tokens?.access_token ?? '';
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  function handleDragStart(item: VisualizationMediaItem, event: DragEvent<HTMLDivElement>) {
    setDraggingId(item.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', item.id);
  }

  function handleDragOverTile(item: VisualizationMediaItem, event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (item.id !== dropTargetId) {
      setDropTargetId(item.id);
    }
  }

  function handleDropOnTile(targetItem: VisualizationMediaItem, event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    const sourceId =
      event.dataTransfer.getData('text/plain') || draggingId;
    setDropTargetId(null);
    setDraggingId(null);
    if (!sourceId || sourceId === targetItem.id) return;

    const currentIds = items.map((m) => m.id);
    const fromIndex = currentIds.indexOf(sourceId);
    const toIndex = currentIds.indexOf(targetItem.id);
    if (fromIndex === -1 || toIndex === -1) return;

    const next = [...currentIds];
    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, sourceId);
    onReorder(next);
  }

  function handleDragEnd() {
    setDraggingId(null);
    setDropTargetId(null);
  }

  return (
    <div className={styles.mediaGrid}>
      {items.map((item) => {
        const isDragging = draggingId === item.id;
        const isDropTarget = dropTargetId === item.id && draggingId !== item.id;
        const isDeleting = deletingId === item.id;
        return (
          <div
            key={item.id}
            className={styles.mediaTile}
            draggable={!isMutating}
            data-dragging={isDragging ? 'true' : undefined}
            data-drop-target={isDropTarget ? 'true' : undefined}
            onDragStart={(e) => handleDragStart(item, e)}
            onDragOver={(e) => handleDragOverTile(item, e)}
            onDragLeave={() => {
              if (dropTargetId === item.id) setDropTargetId(null);
            }}
            onDrop={(e) => handleDropOnTile(item, e)}
            onDragEnd={handleDragEnd}
            title={item.original_filename ?? ''}
          >
            <div className={styles.mediaThumb}>
              <span className={styles.mediaBadge}>
                {item.media_type === 'video' ? t('modeling.mediaVideo') : t('modeling.mediaPhoto')}
              </span>
              <button
                type="button"
                className={styles.mediaDeleteBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                }}
                disabled={isMutating || isDeleting}
                aria-label={t('modeling.deleteMedia')}
              >
                <Icon name="close" size={14} />
              </button>
              <MediaThumbnail
                item={item}
                patientId={patientId}
                treatmentId={treatmentId}
                stageId={stageId}
                token={token}
              />
            </div>
            <div className={styles.mediaCaption}>
              {item.original_filename ?? (item.media_type === 'video' ? t('modeling.mediaVideo') : t('modeling.mediaPhoto'))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Authenticated thumbnail ──────────────────────────────────────────────────

type MediaThumbnailProps = {
  readonly item: VisualizationMediaItem;
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stageId: string;
  readonly token: string;
};

function MediaThumbnail({ item, patientId, treatmentId, stageId, token }: MediaThumbnailProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const isPhoto = item.media_type === 'photo';

  useEffect(() => {
    if (!isPhoto) {
      setIsLoading(false);
      return;
    }
    if (!token) return;

    let objUrl: string | null = null;
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    const path = `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${stageId}/visualization-media/${item.id}/content`;

    fetchAuthenticatedBlob(path, token)
      .then((blob) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [isPhoto, item.id, patientId, stageId, token, treatmentId]);

  if (!isPhoto) {
    return (
      <div className={styles.mediaVideoPlaceholder} aria-hidden>
        <Icon name="play" size={36} />
      </div>
    );
  }

  if (isLoading) {
    return <div className={styles.mediaSkeleton} />;
  }

  if (hasError || !blobUrl) {
    return <div className={styles.mediaError}>!</div>;
  }

  return <img src={blobUrl} alt={item.original_filename ?? ''} className={styles.mediaImg} />;
}
